import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { notasVentaApi } from '../../api/notasVenta'
import EstadoBadge from '../../components/EstadoBadge'
import EstadoBadgePago from '../../components/EstadoBadgePago'
import { useAuth } from '../../context/AuthContext'
import { fmtFecha, fmtFechaHora, fmtHoraDe } from '../../utils/fecha'
import { colors, font, text } from '../../design/tokens'
import { Seccion, Dato, Cargando, ErrorMsg, PagoDot, fmtMoney, LinkMapa, BotonPrimario } from './comunes'
import NotaClienteBanner from '../../components/NotaClienteBanner'
import BotonesCobroWA from '../../components/BotonesCobroWA'
import AccionesRemitoMovil from './AccionesRemitoMovil'

// Detalle móvil de una NV. Reusa GET /notas-venta/{id}. Desde acá también se
// PROCESA (enviar paquete a Presea) si la NV está en borrador y el rol puede.
export default function MovilVentaDetalle() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { rolOperativoVentas } = useAuth()
  const [nv, setNv] = useState(null)
  const [errorWA, setErrorWA] = useState(null)
  const [error, setError] = useState(null)

  const cargar = () => notasVentaApi.obtener(id)
    .then(({ data }) => setNv(data))
    .catch(() => setError('No se pudo cargar la nota de venta.'))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar() }, [id])

  if (error) return <ErrorMsg>{error}</ErrorMsg>
  if (!nv) return <Cargando />

  const d = nv.direccion_envio
  const dirEnvio = d && (d.calle || d.localidad)
    ? [d.calle, d.piso && `piso ${d.piso}`, d.depto && `depto ${d.depto}`, d.localidad, d.provincia, d.codigo_postal].filter(Boolean).join(', ')
    : null
  const dirFiscal = [nv.cliente?.domicilio, nv.cliente?.localidad, nv.cliente?.provincia].filter(Boolean).join(', ')

  return (
    <div>
      {/* Encabezado del documento */}
      <div style={{ margin: '4px 0 2px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: 17, fontWeight: 700, color: colors.text }}>
          {nv.numero_lgr}
        </span>
        <EstadoBadge estado={nv.estado} />
        <PagoDot estado={nv.pago_estado} />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '6px 0 2px' }}>
        {nv.numero_presea && nv.numero_presea !== '0' && <ChipPresea>Factura {nv.numero_presea}</ChipPresea>}
        {nv.numero_remito_presea && <ChipPresea>Remito {nv.numero_remito_presea}</ChipPresea>}
      </div>

      {/* Nota del checkout — instrucción del comprador, arriba de todo. */}
      {nv.nota_cliente && (
        <div style={{ marginTop: 10 }}>
          <NotaClienteBanner nota={nv.nota_cliente} compacto />
        </div>
      )}

      {/* Procesar: mismo circuito que el modal "Enviar paquete" del escritorio */}
      {nv.es_editable && rolOperativoVentas() && (
        <BotonPrimario onClick={() => navigate(`/m/ventas/${nv.id}/procesar`)} style={{ marginTop: 12 }}>
          📦 Procesar — enviar paquete a Presea
        </BotonPrimario>
      )}

      <Seccion titulo="Cliente">
        <div style={{ fontFamily: font.sans, fontWeight: 600, fontSize: 15, color: colors.text, marginBottom: 4 }}>
          {nv.cliente?.nombre || '—'}
        </div>
        <Dato label="Código" mono>{nv.cliente?.codigo_presea || 'Pendiente'}</Dato>
        <Dato label="CUIT" mono>{nv.cliente?.cuit || '—'}</Dato>
        {nv.cliente?.telefono && <Dato label="Teléfono" mono>{nv.cliente.telefono}</Dato>}
      </Seccion>

      <Seccion titulo="Entrega">
        {nv.requiere_entrega === false ? (
          <p style={nota}>✨ Sin entrega — esta venta no despacha mercadería.</p>
        ) : d?.retira ? (
          <p style={nota}>🏬 Retira el cliente{d.sucursal ? ` — sucursal ${d.sucursal}` : ' en depósito'}.</p>
        ) : dirEnvio ? (
          <>
            {d.receptor_nombre && <Dato label="Recibe">{d.receptor_nombre}</Dato>}
            <Dato label="Dirección">{dirEnvio}</Dato>
            <LinkMapa dir={dirEnvio} />
          </>
        ) : (
          <>
            <Dato label="Dirección">{dirFiscal || '—'}</Dato>
            <p style={nota}>Se entrega al domicilio fiscal del cliente.</p>
            {dirFiscal && <LinkMapa dir={dirFiscal} />}
          </>
        )}
      </Seccion>

      {nv.remitos?.length > 0 && (
        <Seccion titulo="Remitos y envíos">
          {nv.remitos.map((r) => (
            <div key={r.id} style={{ borderBottom: '1px solid #f1f5f9', padding: '6px 0' }}>
              <button
                onClick={() => navigate(`/m/logistica/remito/${r.id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
              >
                <span style={{ fontFamily: font.mono, fontWeight: 600, fontSize: 14, color: colors.link }}>
                  {r.transporte === 90 ? 'Remito' : 'Remito T80'} {r.numero_presea || `#${r.id}`}
                </span>
                <span style={{ fontSize: 12, color: colors.textSec, fontFamily: font.sans, textTransform: 'capitalize' }}>{r.estado?.replace('_', ' ')}</span>
                <span style={{ marginLeft: 'auto', color: colors.textMeta }}>›</span>
              </button>
              {rolOperativoVentas() && <AccionesRemitoMovil remito={r} onCambio={cargar} />}
              {(r.notas_envio || []).map((ne) => (
                <button
                  key={ne.id}
                  onClick={() => navigate(`/m/logistica/ne/${ne.id}`)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 'none', padding: '4px 0 0 16px', cursor: 'pointer', textAlign: 'left' }}
                >
                  <span style={{ fontFamily: font.mono, fontSize: 13, color: colors.link }}>NE {ne.numero_ne || `#${ne.id}`}</span>
                  <span style={{ fontSize: 12, color: colors.textSec, fontFamily: font.sans }}>{ne.destinatario || ''}</span>
                  <span style={{ marginLeft: 'auto', color: colors.textMeta }}>›</span>
                </button>
              ))}
            </div>
          ))}
        </Seccion>
      )}

      <Seccion titulo={`Artículos (${nv.items?.length || 0})`}>
        {(nv.items || []).map((it, i) => (
          <div key={i} style={{ padding: '7px 0', borderBottom: i < nv.items.length - 1 ? `1px solid ${colors.borderSoft}` : 'none' }}>
            <div style={{ fontSize: text.body, color: colors.text, fontFamily: font.sans }}>
              {it.descripcion || it.articulo?.descripcion || '—'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
              <span style={{ ...monoSec }}>
                {Number(it.cantidad)} × {fmtMoney(it.precio_final)}
                {Number(it.descuento) > 0 && <span style={{ color: colors.textMeta }}> (−{Number(it.descuento)}%)</span>}
              </span>
              <span style={{ ...monoSec, fontWeight: 700, color: colors.text }}>
                {fmtMoney(Number(it.precio_final) * Number(it.cantidad))}
              </span>
            </div>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10 }}>
          <span style={{ fontSize: text.body, fontWeight: 700, color: colors.text }}>TOTAL</span>
          <span style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: 15, fontWeight: 700, color: colors.text }}>
            {fmtMoney(nv.total)}
          </span>
        </div>
        {nv.monto_a_cobrar != null && Math.abs(Number(nv.monto_a_cobrar) - Number(nv.total)) > 0.01 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 4 }}>
            <span style={{ fontSize: text.sec, color: colors.textSec }}>A cobrar (c/ percepciones)</span>
            <span style={{ ...monoSec, fontWeight: 700 }}>{fmtMoney(nv.monto_a_cobrar)}</span>
          </div>
        )}
      </Seccion>

      {/* Cobro por WhatsApp (WA 1·2·3) del pedido web, para administradores —
          Martin, 26 sep 2026: "necesito poder activar una persecución con WA"
          desde el celular. Mismo componente que en el escritorio. */}
      {nv.whatsapp_puede_enviar && nv.whatsapp && nv.pedido_web_id && (
        <Seccion titulo="Cobro por WhatsApp">
          {nv.whatsapp.telefono_e164 ? (
            <BotonesCobroWA
              columnas
              pedidoId={nv.pedido_web_id}
              whatsapp={nv.whatsapp}
              onActualizado={(w) => setNv(prev => ({ ...prev, whatsapp: w }))}
              onError={(m) => setErrorWA(m)}
            />
          ) : (
            <span style={{ fontSize: text.sec, color: colors.textMeta }}>El pedido no tiene un teléfono válido.</span>
          )}
          {errorWA && <div style={{ marginTop: 6, fontSize: text.sec, color: colors.err }}>{errorWA}</div>}
        </Seccion>
      )}

      {(nv.pagos || []).length > 0 && (
        <Seccion titulo="Recibos">
          {nv.pagos.map((p, i) => (
            <button
              key={p.id || i}
              onClick={() => p.id && navigate(`/m/pagos/${p.id}`)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', width: '100%', background: 'none', border: 'none', borderBottom: i < nv.pagos.length - 1 ? `1px solid ${colors.borderSoft}` : 'none', cursor: 'pointer', textAlign: 'left' }}
            >
              <span style={{ fontSize: text.body, color: colors.link, flex: 1, fontFamily: font.sans, fontWeight: 600 }}>
                {p.medio_pago?.nombre || p.medioPago?.nombre || 'Recibo'}
                {p.numero_presea && <span style={{ ...monoSec, marginLeft: 6 }}>{p.numero_presea}</span>}
              </span>
              <span style={{ ...monoSec, fontWeight: 700, color: colors.text }}>{fmtMoney(p.importe)}</span>
              <EstadoBadgePago estado={p.estado} fallido={!!(p.cobro_fallido || p.cobro_fallido_at)} />
              <span style={{ color: colors.textMeta }}>›</span>
            </button>
          ))}
        </Seccion>
      )}

      {/* Comprobante de transferencia que subió el cliente desde la web —
          mismo criterio que el detalle de escritorio: se abre antes de
          acreditar el pago. */}
      {nv.comprobante_url && (
        <div style={{
          marginTop: 14, padding: '10px 12px', borderRadius: 10,
          background: colors.okBg, border: '1px solid #bbf7d0',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: text.sec, fontWeight: 600, color: colors.ok, fontFamily: font.sans }}>
              📎 Comprobante de transferencia del cliente
            </div>
            {nv.comprobante_subido_at && (
              <div style={{ fontSize: 11.5, color: colors.ok, marginTop: 2, fontFamily: font.sans }}>
                Subido el {fmtFechaHora(nv.comprobante_subido_at)}
              </div>
            )}
          </div>
          <a
            href={nv.comprobante_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              flexShrink: 0, padding: '7px 12px', borderRadius: 8,
              border: `1px solid ${colors.ok}`, color: colors.ok,
              fontSize: 12.5, fontWeight: 600, fontFamily: font.sans,
              textDecoration: 'none', whiteSpace: 'nowrap',
            }}
          >
            Ver ↗
          </a>
        </div>
      )}

      <Seccion titulo="Datos">
        <Dato label="Fecha" mono>{fmtFecha(nv.fecha)}{fmtHoraDe(nv.created_at) ? ` · ${fmtHoraDe(nv.created_at)}` : ''}</Dato>
        <Dato label="Vendedor">{nv.vendedor?.nombre || '—'}</Dato>
        {nv.tipo_factura && <Dato label="Tipo factura" mono>{nv.tipo_factura}</Dato>}
        {nv.origen && nv.origen !== 'lgr' && <Dato label="Origen">{nv.origen}</Dato>}
        {nv.observaciones && (
          <p style={{ ...nota, marginTop: 8, whiteSpace: 'pre-wrap' }}>{nv.observaciones}</p>
        )}
      </Seccion>
    </div>
  )
}

function ChipPresea({ children }) {
  return (
    <span style={{
      padding: '3px 9px', borderRadius: 6, background: colors.linkBg, color: colors.link,
      fontSize: 11.5, fontWeight: 600, fontFamily: font.mono, fontVariantNumeric: 'tabular-nums',
    }}>
      {children}
    </span>
  )
}

const nota = { margin: '2px 0 0', fontSize: text.sec, color: colors.textSec, fontFamily: font.sans, lineHeight: 1.45 }
const monoSec = { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: 12.5, color: colors.textSec }
